import { AssetGroup } from '@play-co/timestep-core';
import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import bitmapFonts from 'src/lib/bitmapFonts';
import { ImageView, View } from '@play-co/timestep-core/ui';
import { Feature } from 'src/replicant/analytics';
import { downscale } from 'src/conf/scale';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { locale } from 'src/StateObserver';
import { assertNever } from 'src/replicant/utils';

type CancelType = 'canceled' | 'failed' | 'fatal';

/**
 * Close handler
 *
 * If needSupport is true, the user should be redirected to the support page
 */
type CloseHandler = (needSupport: boolean) => void;

/**
 * Legacy popup for a canceled or failed purchase
 *
 * @event close: CloseHandler - If needSupport is true, the user should be redirected to the support page
 *
 * @example
 *
 * const needSupport = await openPopupPromise('popupPurchaseCanceled', {});
 *
 * if (needSupport)  openSupportUI();
 */
export default class PopupPurchaseCanceled extends PopupBasicLazy {
  /**
   * Required assets for this popup
   */
  private assets = {
    whiteFrame: 'assets/ui/shared/frames/white_frame.png',
    error: 'assets/ui/shared/icons/alert_icon_red.png',
    warning: 'assets/ui/shared/icons/alert_icon_yellow.png',
  };

  /**
   * Content container
   */
  private content: FlexBoxView;

  /**
   * White frame on middle of popup
   */
  private frame: FlexBoxView;

  /**
   * Warning or error icon in the middle of frame
   */
  private icon: ImageView;

  /**
   * Contact support button
   */
  private actionButton: ButtonScaleViewWithText;

  /**
   * Load assets on demand
   */
  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup(Object.values(this.assets));
  }

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      zIndex: 10000,
      width: downscale(900),
      height: downscale(777),
      darkerBg: true,
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_CANCEL_PURCHASE._,
      },
    });

    this.content = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        alignContent: 'center',
      },
    });

    // White frame
    this.frame = new FlexBoxView({
      superview: this.content,
      width: downscale(734),
      height: downscale(420),
      flex: {
        marginTop: 100,
        marginBottom: 20,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        alignContent: 'center',
      },
    });

    // Frame bg
    new FlexBoxImageScaleView({
      superview: this.frame,
      image: this.assets.whiteFrame,
      width: this.frame.style.width,
      height: this.frame.style.height,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
      flex: {
        position: 'absolute',
      },
    });

    // Warning icon
    this.icon = new ImageView({
      superview: this.frame,
      image: this.assets.warning,
      width: downscale(258),
      height: downscale(258),
    });

    this.title.updateOpts({ size: locale() === 'ja' ? 27 : 33 });

    // Message text
    this.message.updateOpts({
      superview: this.frame,
      width: this.frame.style.width,
      height: downscale(100),
      size: 26,
      color: '#9C693A',
      offsetY: 15,
    });

    // Contact support button
    this.actionButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.content,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 36,
      font: bitmapFonts('BodyBig'),
      width: downscale(560),
      height: downscale(140),
      localeText: () => i18n('purchaseCanceled.canceled.action'),
      onClick: async () => {
        opts.close(true);
      },
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_CANCEL_PURCHASE.SUPPORT,
      },
    });
  }

  /** Show the legacy popup for the requested purchase result. */
  public init(opts: { type?: CancelType }): void {
    const type = opts.type ?? 'canceled';

    switch (type) {
      case 'failed':
        super.init({
          title: i18n('purchaseCanceled.failed.title'),
          message: i18n('purchaseCanceled.failed.message'),
        });
        this.icon.updateOpts({ image: this.assets.error });
        break;

      case 'fatal':
        super.init({
          title: i18n('purchaseCanceled.failedFatal.title'),
          message: i18n('purchaseCanceled.failedFatal.message'),
        });
        this.icon.updateOpts({ image: this.assets.error });
        break;

      case 'canceled':
        super.init({
          title: i18n('purchaseCanceled.canceled.title'),
          message: i18n('purchaseCanceled.canceled.message'),
        });
        this.icon.updateOpts({ image: this.assets.warning });
        break;

      default:
        assertNever(type);
    }
  }
}
